import React from 'react';
import { ShoppingCart, User, Heart, Search, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

// Template 12: IMPACT - Bold Typography, Full-width Images (Inspired by Shopify's Impact theme)
const Template12 = () => {
    const navigate = useNavigate();

    return (
        <div className="bg-white min-h-screen font-sans">
            {/* Bold Header */}
                        {/* Header with Navigation */}
            <header className="bg-white border-b border-gray-200 sticky top-0 z-50 shadow-sm">
                <div className="container mx-auto px-4 lg:px-8">
                    <div className="flex justify-between items-center py-4 lg:py-5">
                        <h1 onClick={() => navigate('/template12')} className="text-2xl lg:text-3xl font-bold text-gray-900 cursor-pointer tracking-tight">
                            LUXE
                        </h1>
                        <div className="flex items-center gap-4 lg:gap-6">
                            <Search className="w-5 h-5 text-gray-900 cursor-pointer hover:text-gray-600 transition-colors" />
                            <Heart className="w-5 h-5 text-gray-900 cursor-pointer hover:text-gray-600 transition-colors" />
                            <div className="relative cursor-pointer" onClick={() => navigate('/template12/cart')}>
                                <ShoppingCart className="w-5 h-5 text-gray-900 hover:text-gray-600 transition-colors" />
                                <span className="absolute -top-2 -right-2 bg-gray-900 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center font-medium">0</span>
                            </div>
                            <User className="w-5 h-5 text-gray-900 cursor-pointer hover:text-gray-600 transition-colors" onClick={() => navigate('/template12/profile')} />
                        </div>
                    </div>
                    <nav className="border-t border-gray-200">
                        <div className="flex justify-center items-center gap-8 lg:gap-12 py-4">
                            <button onClick={() => navigate('/template12')} className="text-sm lg:text-base font-medium text-gray-900 hover:text-gray-600 transition-colors uppercase tracking-wide">Home</button>
                            <button onClick={() => navigate('/template12/shop')} className="text-sm lg:text-base font-medium text-gray-900 hover:text-gray-600 transition-colors uppercase tracking-wide">Shop</button>
                            <button onClick={() => navigate('/template12/contact')} className="text-sm lg:text-base font-medium text-gray-900 hover:text-gray-600 transition-colors uppercase tracking-wide">Contact</button>
                            <button onClick={() => navigate('/template12/orders')} className="text-sm lg:text-base font-medium text-gray-900 hover:text-gray-600 transition-colors uppercase tracking-wide">Orders</button>
                        </div>
                    </nav>
                </div>
            </header>

            {/* Full-Width Hero with Bold Text */}
            <section className="relative h-screen bg-gray-900">
                <div className="absolute inset-0 flex items-center justify-center px-6">
                    <div className="text-center text-white max-w-5xl">
                        <h2 className="text-7xl md:text-9xl font-black leading-none mb-8 tracking-tighter">
                            MAKE AN<br/>IMPACT
                        </h2>
                        <p className="text-2xl md:text-3xl font-bold mb-12">
                            Bold Designs. Bolder Statements.
                        </p>
                        <button className="group px-12 py-6 bg-white text-black text-lg font-black hover:bg-black hover:text-white transition-all">
                            <span>DISCOVER NOW</span>
                            <ArrowRight className="inline-block ml-3 group-hover:translate-x-2 transition-transform" size={24} />
                        </button>
                    </div>
                </div>
            </section>

            {/* Collection Tiles - Full Width */}
            <section className="grid grid-cols-1 md:grid-cols-2">
                {[
                    { title: 'WOMEN', image: 'https://placehold.co/1200x800/E0E0E0/333333?text=WOMEN' },
                    { title: 'MEN', image: 'https://placehold.co/1200x800/D0D0D0/333333?text=MEN' }
                ].map((item, index) => (
                    <div key={index} className="relative h-[600px] overflow-hidden group cursor-pointer">
                        <img
                            src={item.image}
                            alt={item.title}
                            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                        />
                        <div className="absolute inset-0 bg-black/40 group-hover:bg-black/20 transition-opacity"></div>
                        <div className="absolute inset-0 flex items-center justify-center">
                            <div className="text-center text-white">
                                <h3 className="text-6xl md:text-8xl font-black mb-6 tracking-tighter">{item.title}</h3>
                                <button className="px-8 py-4 border-4 border-white text-2xl font-black hover:bg-white hover:text-black transition-all">
                                    SHOP {item.title}
                                </button>
                            </div>
                        </div>
                    </div>
                ))}
            </section>

            {/* Product Grid */}
            <section className="w-full bg-gray-50 py-16 lg:py-24">
                <div className="max-w-7xl mx-auto px-6">
                    <div className="text-center mb-12">
                        <h2 className="text-4xl lg:text-6xl font-bold text-gray-900 mb-4">All Products</h2>
                        <p className="text-xl text-gray-600">Discover our latest collection</p>
                    </div>

                    <div className="w-full max-w-none">
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6 lg:gap-8">
                            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(id => (
                                <div key={id} className="bg-white rounded-xl overflow-hidden shadow-sm border border-gray-200 hover:shadow-2xl hover:-translate-y-2 hover:border-gray-900 transition-all duration-300 group">
                                    <div className="aspect-[4/5] overflow-hidden relative bg-gray-50">
                                        <img
                                            src={`https://placehold.co/400x500/F0F0F0/333333?text=Product+${id}`}
                                            alt={`Product ${id}`}
                                            className="object-cover object-center w-full h-full group-hover:scale-110 transition-transform duration-700"
                                        />
                                    </div>
                                    <div className="p-4 lg:p-6">
                                        <h3 className="text-lg font-semibold text-gray-900 mb-2 line-clamp-2">Product Title {id}</h3>
                                        <p className="text-xl lg:text-2xl font-bold text-gray-900 mb-4">${(69 + id * 10)}.00</p>
                                        <button className="w-full bg-gray-900 text-white py-3 px-6 rounded-lg font-medium hover:bg-gray-800 transition-all duration-300">
                                            Add to Cart
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Statement Section */}
            <section className="bg-black text-white py-32">
                <div className="max-w-5xl mx-auto px-6 text-center">
                    <h2 className="text-6xl md:text-8xl font-black mb-8 leading-none tracking-tighter">
                        DESIGNED TO<br/>STAND OUT
                    </h2>
                    <p className="text-2xl md:text-3xl font-bold text-gray-300 mb-12">
                        Not for everyone. Just for you.
                    </p>
                    <button className="px-12 py-6 bg-white text-gray-900 text-xl font-black hover:bg-gray-100 transition-all">
                        EXPLORE COLLECTION
                    </button>
                </div>
            </section>

            {/* Categories with Overlay Text */}
            <section className="grid grid-cols-1 md:grid-cols-3">
                {[
                    { title: 'TOPS', image: 'https://placehold.co/800x1000/E0E0E0/333333?text=TOPS' },
                    { title: 'BOTTOMS', image: 'https://placehold.co/800x1000/D0D0D0/333333?text=BOTTOMS' },
                    { title: 'SHOES', image: 'https://placehold.co/800x1000/C0C0C0/333333?text=SHOES' }
                ].map((cat, index) => (
                    <div key={index} className="relative h-96 overflow-hidden group cursor-pointer">
                        <img
                            src={cat.image}
                            alt={cat.title}
                            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                        <div className="absolute bottom-12 left-12">
                            <h3 className="text-5xl font-black text-white mb-4 tracking-tighter">{cat.title}</h3>
                            <button className="text-white text-lg font-bold underline underline-offset-8">
                                SHOP NOW →
                            </button>
                        </div>
                    </div>
                ))}
            </section>

            {/* Footer */}
                        {/* Footer */}
            <footer className="bg-gray-900 text-white mt-0">
                <div className="container mx-auto px-4 lg:px-8 py-12 lg:py-16">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-8 lg:gap-12">
                        <div>
                            <h3 className="text-2xl font-bold mb-4 tracking-tight">LUXE</h3>
                            <p className="text-gray-400 text-sm leading-relaxed">Contemporary design excellence.</p>
                        </div>
                        <div>
                            <h4 className="font-semibold mb-4 text-sm uppercase tracking-wide">Quick Links</h4>
                            <ul className="space-y-2 text-sm">
                                <li><span onClick={() => navigate('/template12')} className="text-gray-400 hover:text-white cursor-pointer transition-colors">Home</span></li>
                                <li><span onClick={() => navigate('/template12/shop')} className="text-gray-400 hover:text-white cursor-pointer transition-colors">Shop</span></li>
                                <li><span onClick={() => navigate('/template12/contact')} className="text-gray-400 hover:text-white cursor-pointer transition-colors">Contact</span></li>
                                <li><span onClick={() => navigate('/template12/orders')} className="text-gray-400 hover:text-white cursor-pointer transition-colors">Orders</span></li>
                            </ul>
                        </div>
                        <div>
                            <h4 className="font-semibold mb-4 text-sm uppercase tracking-wide">Customer Service</h4>
                            <ul className="space-y-2 text-sm">
                                <li className="text-gray-400">Shipping & Returns</li>
                                <li className="text-gray-400">Privacy Policy</li>
                                <li className="text-gray-400">Terms & Conditions</li>
                                <li className="text-gray-400">FAQ</li>
                            </ul>
                        </div>
                        <div>
                            <h4 className="font-semibold mb-4 text-sm uppercase tracking-wide">Connect</h4>
                            <p className="text-gray-400 text-sm">hello@luxe.com</p>
                        </div>
                    </div>
                    <div className="border-t border-gray-800 mt-12 pt-8">
                        <p className="text-center text-gray-500 text-sm">© 2025 LUXE. All rights reserved.</p>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default Template12;
